import { convertNumber } from './number/numberUtils';
import { convertColor } from './color/colorUtils';
import { convertResistance } from './resistance/resistanceUtils';
import { convertElectricalCharge } from './electricalCharge/electricalChargeUtils';
import { convertEnergy } from './energy/energyUtils';
import { convertFrequency } from './frequency/frequencyUtils';
import { convertImage } from './image/imageUtils';
import { convertLength } from './length/lengthUtils';
import { convertPower } from './power/powerUtils';
import { convertVoltage } from './voltage/voltageUtils';
import { convertWeight } from './weight/weightUtils';
import { convertTemperature } from './temperature/temperatureUtils';
import { error } from '../../utils/logger';

export const conversionData = {
  weight: {
    name: 'Weight Conversion',
    units: [
      'Grams', 'Kilograms', 'Milligrams', 'Micrograms', 'Metric Tons',
      'Ounces', 'Pounds', 'Stone', 'US Tons', 'Imperial Tons',
      'Carats', 'Pennyweights', 'Troy Ounces', 'Drams',
      'Grains', 'Atomic Mass Units', 'Daltons',
      'Slugs', 'Earth Masses', 'Solar Masses'
    ],
    convert: convertWeight,
  },
  length: {
    name: 'Length Conversion',
    units: [
      'Nanometers', 'Micrometers', 'Millimeters', 'Centimeters', 'Meters', 'Kilometers',
      'Inches', 'Feet', 'Yards', 'Miles', 'Nautical Miles',
      'Light Years', 'Astronomical Units',
      'Angstroms', 'Microns', 'Mils',
      'Fathoms', 'Furlongs', 'Chains', 'Rods', 'Links',
      'Parsecs'
    ],
    convert: convertLength,
  },
  power: {
    name: 'Power Conversion',
    units: [
      'Watts', 'Kilowatts', 'Megawatts', 'Gigawatts',
      'Horsepower (Mechanical)', 'Horsepower (Metric)',
      'BTU/hour', 'Foot-pounds/minute', 'Calories/second',
      'Joules/second', 'Ergs/second', 'Dyne-centimeters/second',
      'Newton-meters/second', 'Kilogram-meters/second',
      'Volt-amperes', 'Decibel-milliwatts'
    ],
    convert: convertPower,
  },
  voltage: {
    name: 'Voltage Conversion',
    units: [
      'Volts', 'Microvolts', 'Millivolts', 'Kilovolts', 'Megavolts', 'Gigavolts',
      'Statvolts', 'Abvolts', 'EMU of electric potential',
      'ESU of electric potential', 'Webers per meter'
    ],
    convert: convertVoltage,
  },
  energy: {
    name: 'Energy Conversion',
    units: [
      'Joules', 'Kilojoules', 'Calories', 'Kilocalories',
      'Watt-hours', 'Kilowatt-hours', 'Electron-volts',
      'British Thermal Units (BTU)', 'Therms',
      'Foot-pounds', 'Ergs'
    ],
    convert: convertEnergy,
  },
  electricCharge: {
    name: 'Electric Charge Conversion',
    units: ['Coulombs', 'Picocoulombs', 'Nanocoulombs', 'Microcoulombs', 'Millicoulombs', 'Ampere-hours'],
    convert: convertElectricalCharge,
  },
  number: {
    name: 'Number Systems',
    units: ['Decimal', 'Binary', 'Octal', 'Hexadecimal', 'Base-36', 'Base-64', 'Roman Numeral', 'Scientific Notation'],
    convert: convertNumber,
  },
  frequency: {
    name: 'Frequency Conversion',
    units: [
      'Hertz', 'Kilohertz', 'Megahertz', 'Gigahertz', 'Terahertz',
      'Cycles per second', 'Radians per second',
      'Revolutions per minute (RPM)', 'Beats per minute (BPM)',
      'Wavelength (meters)', 'Wavenumber (cm⁻¹)'
    ],
    convert: convertFrequency,
  },
  temperature: {
    name: 'Temperature Conversion',
    units: [
      'Celsius', 'Fahrenheit', 'Kelvin', 'Rankine', 
      'Réaumur', 'Delisle', 'Newton', 'Rømer'
    ],
    convert: convertTemperature,
  },
  color: {
    name: 'Color Conversion',
    units: ['RGB', 'HEX', 'HSL'],
    convert: convertColor,
  },
  resistance: {
    name: 'Resistance Conversion',
    units: [
      'Ohms', 'Kilohms', 'Megohms', 'Gigaohms',
      'Milliohms', 'Microohms', 'Nanoohms',
      'Abohms', 'Statohms'
    ],
    convert: convertResistance,
  },
  image: {
    name: 'Image Conversion',
    units: [
      'Pixels', 'Inches', 'Centimeters',
      'PPI (Pixels per Inch)', 'DPI (Dots per Inch)', 'PPCM (Pixels per Centimeter)',
      'Megapixels', 'Aspect Ratio'
    ],
    convert: convertImage,
  },
  'electric-charge': {
    units: [
      'Coulomb (C)',
      'Millicoulomb (mC)',
      'Microcoulomb (µC)',
      'Nanocoulomb (nC)',
      'Ampere-hour (Ah)',
      'Milliampere-hour (mAh)',
      'Elementary charge (e)',
      'Faraday (F)',
      'Abcoulomb (abC)',
      'Statcoulomb (statC)'
    ],
    conversions: {
      'Coulomb (C)': {
        'Millicoulomb (mC)': value => value * 1000,
        'Microcoulomb (µC)': value => value * 1e6,
        'Nanocoulomb (nC)': value => value * 1e9,
        'Ampere-hour (Ah)': value => value / 3600,
        'Milliampere-hour (mAh)': value => value * 1000 / 3600,
        'Elementary charge (e)': value => value * 6.242e18,
        'Faraday (F)': value => value / 96485,
        'Abcoulomb (abC)': value => value * 0.1,
        'Statcoulomb (statC)': value => value * 2.998e9
      },
      'Millicoulomb (mC)': {
        'Coulomb (C)': value => value / 1000,
        'Microcoulomb (µC)': value => value * 1000,
        'Nanocoulomb (nC)': value => value * 1e6,
        'Ampere-hour (Ah)': value => value / (3600 * 1000),
        'Milliampere-hour (mAh)': value => value / 3.6,
        'Elementary charge (e)': value => value * 6.242e15,
        'Faraday (F)': value => value / (96485 * 1000),
        'Abcoulomb (abC)': value => value * 0.0001,
        'Statcoulomb (statC)': value => value * 2.998e6
      },
      // Add other conversion pairs as needed
    },
    defaultUnits: {
      from: 'Coulomb (C)',
      to: 'Ampere-hour (Ah)'
    },
    format: {
      precision: 6,
      notation: 'scientific'
    },
    validation: {
      min: 0,
      max: Number.MAX_SAFE_INTEGER,
      regex: /^[0-9.e+-]+$/
    }
  }
};

// Add a validation check
Object.entries(conversionData).forEach(([category, data]) => {
  if (!data.units || !Array.isArray(data.units) || data.units.length === 0) {
    error(`Invalid conversion data for category: ${category}`, data);
  }
});
